import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { MultiValue } from 'react-select';

import {
  AnnouncementsByTournamentId,
  getTimeFilterOptions,
  SportOption,
  TimeOption,
} from './types';

import { API, useFetch } from 'app/api';
import { IconSize } from 'app/design-system/types/Icon';
import { useTranslation } from 'app/lang';
import { Skeleton } from 'app/modules/Betting';
import Multi, { MultiChangeHandler } from 'app/widgets/Dropdown/Multi';
import { ChangeHandler } from 'app/widgets/Dropdown/Single';
import EmptyGame from 'app/widgets/EmptyGame';
import ErrorBoundary from 'app/widgets/ErrorBoundary';

import { sportIds } from '../../selectors/entities';
import GridHeader from '../components/GridHeader';
import AnnouncementTable from './components/AnnouncementTable';
import FilterByTime from './components/FilterByTime';
import { getTimeToFetch } from './get-time-to-fetch-utils';
import { getAnnouncementsAndOptions } from './utils/get-announcements-and-options-utils';

import dictionary from './Announcement.dictionary.json';

import styles from './Announcement.module.scss';

const AnnouncementContent = () => {
  const { t } = useTranslation();
  const timeFilterOptions = getTimeFilterOptions(t);
  const [needFetch, setNeedFetch] = useState(false);
  const bettingSportIds = useSelector(sportIds);
  const { data, requested, makeRequest } = useFetch(API.live.schedule.getLiveSchedule, {
    skipSSR: true,
  });
  const [timeFilter, setTimeFilter] = useState<TimeOption>(timeFilterOptions[0]);
  const [sportTypes, setSportTypes] = useState<MultiValue<SportOption>>([]);
  useEffect(() => {
    makeRequest();
  }, [needFetch]);

  useEffect(() => {
    if (data) {
      const timestamps = data.map((item) => item.startDate);
      const time = getTimeToFetch(timestamps);

      const timer = setTimeout(() => setNeedFetch((prev) => !prev), time);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [data]);

  const handleTimeFilterChange: ChangeHandler<TimeOption> = useCallback((option) => {
    if (!option || !('value' in option)) {
      console.error('Live -> handleTimeFilterChange -> `option` not found', option);
      return;
    }

    setTimeFilter(option);
  }, []);

  const handleSportTypesChange: MultiChangeHandler<SportOption> = useCallback((options) => {
    if (!options) {
      console.error('Live -> handleSportTypesChange -> `options` not found', options);
      return;
    }

    setSportTypes(options);
  }, []);

  const { announcements, options } = useMemo(() => {
    return getAnnouncementsAndOptions(data, timeFilter.value, sportTypes);
  }, [data, timeFilter.value, sportTypes]);

  if (data === null && !requested) {
    return <Skeleton />;
  }

  if (data && !data.length) {
    return <EmptyGame />;
  }

  const renderMatches =
  (announcementsByTournament: AnnouncementsByTournamentId, sportId: number) => {
    const announcementsByStages: JSX.Element[] = [];

    announcementsByTournament.forEach((matches, stageId) => {
      announcementsByStages.push(
        <AnnouncementTable
          key={stageId}
          sportId={sportId}
          matches={matches} />,
      );
    });

    return announcementsByStages;
  };

  const renderAnnouncements = () => {
    const announcementsBySport: JSX.Element[] = [];

    announcements.forEach((content, sportId) => {
      const shouldBeLink = bettingSportIds.includes(sportId);
      announcementsBySport.push(
        <li key={sportId}>
          <GridHeader sportId={sportId}
                      eventName={content.sportName}
                      shouldBeLink={shouldBeLink}
                      showColumns={false} />
          <ul className={styles.tournamentsList}>
            {renderMatches(content.data, sportId)}
          </ul>
        </li>,
      );
    });

    return announcementsBySport;
  };

  return (
    <ErrorBoundary>
      <div data-test="content-container"
           className={styles.announcementContainer}>
        <div className={styles.announcementHeader}>
          <h4 className={styles.announcementTitle}>{t(dictionary.title)}</h4>

          <div className={styles.filtersContainer}>
            <Multi<SportOption> isMulti
                                openMenuOnFocus
                                closeMenuOnSelect={false}
                                isSearchable={false}
                                className={styles.sportsFilter}
                                indicatorSize={IconSize.MEDIUM}
                                placeholder={t(dictionary.placeholder)}
                                value={sportTypes}
                                options={options}
                                onChange={handleSportTypesChange} />

            <FilterByTime value={timeFilter}
                          className={styles.dateFilter}
                          onChange={handleTimeFilterChange} />
          </div>
        </div>

        {(announcements && announcements.size) ? (
          <ul className={styles.sportsList}>
            {renderAnnouncements()}
          </ul>
        ) : (
          <EmptyGame typeText="noFilteredEvents" />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default AnnouncementContent;
